/**
 * `EditorProvider` — the configured Tiptap editor every consumer mounts.
 * It owns the extension set (StarterKit trimmed, markdown round-tripping,
 * the slash menu) and renders the slash popup.
 *
 * Toolbar components read the editor out of Tiptap's own context via
 * `useCurrentEditor`, so they take no editor prop.
 */
import Blockquote from "@tiptap/extension-blockquote";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { TextStyleKit } from "@tiptap/extension-text-style";
import Typography from "@tiptap/extension-typography";
import { CharacterCount, Placeholder } from "@tiptap/extensions";
import { Markdown } from "@tiptap/markdown";
import {
  ReactRenderer,
  EditorProvider as TiptapEditorProvider,
  type EditorProviderProps as TiptapEditorProviderProps,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { defaultSlashSuggestions, rankSlashSuggestions, Slash, type SuggestionItem } from "./slash";

type EditorSlashMenuProps = {
  items: SuggestionItem[];
  activeIndex: number;
  command: (item: SuggestionItem) => void;
};

const slashOptionId = (index: number) => `slash-command-option-${index}`;

/**
 * The menu renders the active row it is given and owns no selection state.
 *
 * It used to be a cmdk `<Command>`, which keeps selection internally and
 * re-selects the first row only when ITS OWN search value changes. Filtering
 * happens upstream in the suggestion's `items()`, so that value never changed:
 * swapping the list left the old selection pointing at a row that no longer
 * existed, nothing re-selected, and Enter did nothing until you pressed an
 * arrow key. Selection now lives in the render closure below, exactly as the
 * mention menu does it.
 */
const EditorSlashMenu = ({ items, activeIndex, command }: EditorSlashMenuProps) => {
  if (items.length === 0) {
    return <div className="rounded-md border bg-popover p-4 text-muted-foreground text-sm shadow">No results</div>;
  }

  return (
    <div className="max-h-80 overflow-y-auto rounded-md border bg-popover p-1 shadow" role="listbox">
      {items.map((item, index) => (
        // biome-ignore lint/a11y/useFocusableInteractive: rows are intentionally non-focusable — focus stays in the editor so typing keeps narrowing the query; the active row is driven by keyboard state, not DOM focus.
        <div
          aria-selected={index === activeIndex}
          className={cn(
            "flex cursor-pointer items-center gap-3 rounded-sm p-1 pr-3",
            index === activeIndex && "bg-accent text-accent-foreground",
          )}
          id={slashOptionId(index)}
          key={item.title}
          // mousedown, not click: the editor must not lose the selection the
          // command is about to act on.
          onMouseDown={(event) => {
            event.preventDefault();
            command(item);
          }}
          role="option"
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded border bg-secondary">
            <item.icon className="text-muted-foreground" size={16} />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-sm">{item.title}</span>
            <span className="text-muted-foreground text-xs">{item.description}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

/** The quote's look. Exported so a block that takes the quote's place can wear it. */
export const EDITOR_QUOTE_CLASS = "border-l border-l-2 pl-2";

export type EditorProviderProps = TiptapEditorProviderProps & {
  className?: string;
  /**
   * False stops offering the quote: no `>` shortcut, no keyboard shortcut, no
   * slash item. Quotes already in the content still load and save as quotes.
   */
  quote?: boolean;
  limit?: number;
  placeholder?: string;
};

export const EditorProvider = ({
  className,
  extensions,
  quote = true,
  limit,
  placeholder,
  content,
  ...props
}: EditorProviderProps) => {
  const defaultExtensions = [
    StarterKit.configure({
      // Code blocks are not a thing this editor offers. The inline `code` mark
      // below is, and is separate.
      codeBlock: false,
      bulletList: {
        HTMLAttributes: {
          class: cn("list-outside list-disc pl-4"),
        },
      },
      orderedList: {
        HTMLAttributes: {
          class: cn("list-outside list-decimal pl-4"),
        },
      },
      listItem: {
        HTMLAttributes: {
          class: cn("leading-normal"),
        },
      },
      blockquote: quote ? { HTMLAttributes: { class: cn(EDITOR_QUOTE_CLASS) } } : false,
      code: {
        HTMLAttributes: {
          class: cn("rounded-md bg-muted px-1.5 py-1 font-medium font-mono"),
          spellcheck: "false",
        },
      },
      horizontalRule: {
        HTMLAttributes: {
          class: cn("mt-4 mb-6 border-muted-foreground border-t"),
        },
      },
      dropcursor: {
        color: "var(--border)",
        width: 4,
      },
    }),
    ...(quote
      ? []
      : [
          // Kept out of the menus, never out of the schema: a node the schema
          // lacks is dropped on load, and the next save writes the note without it.
          Blockquote.extend({
            addInputRules: () => [],
            addKeyboardShortcuts: () => ({}),
          }).configure({ HTMLAttributes: { class: cn(EDITOR_QUOTE_CLASS) } }),
        ]),
    Typography,
    Placeholder.configure({
      placeholder,
      showOnlyCurrent: false,
      // Default `true` hides the placeholder when the editor is non-editable.
      // For lock-gated editors that means the placeholder only appears after
      // the user clicks in and the lock acquires — too late. Always show.
      showOnlyWhenEditable: false,
      emptyEditorClass: "is-editor-empty",
    }),
    CharacterCount.configure({
      limit,
    }),
    Superscript,
    Subscript,
    Slash.configure({
      suggestion: {
        items: async ({ editor, query, signal }) =>
          rankSlashSuggestions(
            (await defaultSlashSuggestions({ editor, query, signal })).filter(
              (item) => quote || item.title !== "Quote",
            ),
            query,
          ),
        char: "/",
        render: () => {
          let component: ReactRenderer<EditorSlashMenuProps> | undefined;
          let popup: TippyInstance | undefined;
          let latestItems: SuggestionItem[] = [];
          let latestCommand: ((item: SuggestionItem) => void) | undefined;
          let activeIndex = 0;

          const render = () => {
            component?.updateProps({ items: latestItems, activeIndex, command: latestCommand });
            document.getElementById(slashOptionId(activeIndex))?.scrollIntoView({ block: "nearest" });
          };

          return {
            onStart: (onStartProps) => {
              latestItems = onStartProps.items;
              latestCommand = onStartProps.command;
              activeIndex = 0;

              component = new ReactRenderer(EditorSlashMenu, {
                props: { items: latestItems, activeIndex, command: latestCommand },
                editor: onStartProps.editor,
              });

              popup = tippy(document.body, {
                getReferenceClientRect: () => onStartProps.clientRect?.() || new DOMRect(),
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
              });
            },

            onUpdate(onUpdateProps) {
              latestItems = onUpdateProps.items;
              latestCommand = onUpdateProps.command;
              // The query narrowed the list, so whatever was active may be gone
              // — start again at the top, which is what makes type-then-Enter work.
              activeIndex = 0;
              render();

              popup?.setProps({
                getReferenceClientRect: () => onUpdateProps.clientRect?.() || new DOMRect(),
              });
            },

            onKeyDown(onKeyDownProps) {
              const { key } = onKeyDownProps.event;

              if (key === "Escape") {
                popup?.hide();
                component?.destroy();
                return true;
              }

              if (latestItems.length === 0) {
                return false;
              }

              if (key === "ArrowDown") {
                activeIndex = (activeIndex + 1) % latestItems.length;
                render();
                return true;
              }

              if (key === "ArrowUp") {
                activeIndex = (activeIndex - 1 + latestItems.length) % latestItems.length;
                render();
                return true;
              }

              if (key === "Enter" || key === "Tab") {
                const active = latestItems[activeIndex];
                if (active) {
                  latestCommand?.(active);
                }
                // Consumed either way, so Enter never reaches StarterKit's keymap
                // and splits the line underneath the command.
                return true;
              }

              return false;
            },

            onExit() {
              popup?.destroy();
              component?.destroy();
              popup = undefined;
              component = undefined;
            },
          };
        },
      },
    }),
    TaskList.configure({
      HTMLAttributes: {
        // 17px = the width of the checkbox + the gap between the checkbox and the text
        class: "before:translate-x-[17px]",
      },
    }),
    TaskItem.configure({
      HTMLAttributes: {
        // `accent-(--primary)` colors the native checkbox's checked fill with the
        // theme primary (instead of the browser default blue).
        class: "flex items-start gap-1 [&_input[type=checkbox]]:accent-(--primary)",
      },
    }),
    Markdown,
  ];

  return (
    <TooltipProvider>
      <div className={cn(className, "[&_.ProseMirror-focused]:outline-none")}>
        <TiptapEditorProvider
          extensions={[...defaultExtensions, TextStyleKit, ...(extensions ?? [])]}
          immediatelyRender={false}
          content={content || undefined}
          {...(typeof content === "string" && content ? { contentType: "markdown" } : {})}
          {...props}
        />
      </div>
    </TooltipProvider>
  );
};
